import { Controller } from "@hotwired/stimulus"

// Searchable select - replaces a native <select> with a filterable dropdown.
// Useful when option lists are large (100s+).
//
// Usage:
//   <div data-controller="searchable-select" data-searchable-select-options-value='[{"value":"1","label":"Foo"}]'>
//     <input type="hidden" data-searchable-select-target="hidden" name="field_name" value="">
//     <input type="text" data-searchable-select-target="input" placeholder="Search...">
//     <div data-searchable-select-target="dropdown" class="hidden">
//       <div data-searchable-select-target="list"></div>
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["input", "hidden", "dropdown", "list"]
  static values = {
    options: Array, // [{value, label}]
    selected: String
  }

  connect() {
    this.isOpen = false
    this.selectedIndex = 0
    this.filtered = [...(this.optionsValue || [])]

    // Set initial display text from selected value
    if (this.selectedValue) {
      const match = this.optionsValue.find(o => String(o.value) === String(this.selectedValue))
      if (match) this.inputTarget.value = match.label
    }

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.isOpen) {
      this.close()
    }
  }

  focus() {
    this.open()
    this.inputTarget.select()
  }

  input() {
    if (this.isOpen) {
      this.filter()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.filter()
    this.resetDropdownPosition()
    this.dropdownTarget.classList.remove("hidden")
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")

    // Restore display text if nothing was picked
    const current = this.optionsValue.find(o => String(o.value) === String(this.hiddenTarget.value))
    this.inputTarget.value = current ? current.label : ""
  }

  resetDropdownPosition() {
    const dropdown = this.dropdownTarget
    dropdown.style.position = ""
    dropdown.style.left = ""
    dropdown.style.top = ""
    dropdown.style.width = ""
    dropdown.style.marginTop = ""
    dropdown.style.maxHeight = ""
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()
    // Match against the full search haystack (includes alternate names) when
    // provided, falling back to the visible label for simple option lists.
    this.filtered = query === ""
      ? [...(this.optionsValue || [])]
      : (this.optionsValue || []).filter(o => (o.search || o.label).toLowerCase().includes(query))

    this.selectedIndex = 0
    this.render()
  }

  handleKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!this.isOpen) { this.open(); return }
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelection(-1)
        break
      case "Enter":
        event.preventDefault()
        this.selectCurrent()
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  moveSelection(delta) {
    if (this.filtered.length === 0) return
    const previousIndex = this.selectedIndex
    this.selectedIndex = (this.selectedIndex + delta + this.filtered.length) % this.filtered.length
    if (!this.hasListTarget) return
    if (!this.listTarget.children[previousIndex] || !this.listTarget.children[this.selectedIndex]) {
      this.render()
      return
    }
    for (const index of new Set([previousIndex, this.selectedIndex])) {
      const row = this.listTarget.children[index]
      row.className = this.optionClass(index)
      row.setAttribute("aria-selected", String(index === this.selectedIndex))
      row.innerHTML = this.optionContent(this.filtered[index], index)
    }
    this.scrollHighlightedIntoView()
  }

  selectCurrent() {
    if (this.filtered.length === 0) return
    const option = this.filtered[this.selectedIndex]
    if (option) this.pick(option)
  }

  selectItem(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    const option = this.optionsValue.find(o => String(o.value) === String(value))
    if (option) this.pick(option)
  }

  pick(option) {
    this.hiddenTarget.value = option.value
    this.inputTarget.value = option.label
    this.selectedValue = String(option.value)
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  clear(event) {
    if (event) event.preventDefault()

    this.hiddenTarget.value = ""
    this.inputTarget.value = ""
    this.selectedValue = ""
    this.filtered = [...(this.optionsValue || [])]
    this.selectedIndex = 0
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    if (this.isOpen) this.render()
  }

  render() {
    if (!this.hasListTarget) return

    if (this.filtered.length === 0) {
      this.listTarget.innerHTML = `<div class="empty-line"><span class="empty-line__title">No matches</span></div>`
      return
    }

    this.listTarget.innerHTML = this.filtered.map((option, index) => {
      const isHighlighted = index === this.selectedIndex
      return `
        <div class="${this.optionClass(index)}"
             aria-selected="${isHighlighted}"
             data-action="click->searchable-select#selectItem"
             data-value="${this.escapeAttr(String(option.value))}">
          ${this.optionContent(option, index)}
        </div>`
    }).join("")

    this.scrollHighlightedIntoView()
  }

  // The picker's option rows (docs/ui_style_guide.md, "Pickers"): the
  // keyboard highlight is the sand wash, the chosen value carries an ink check.
  optionClass(index) {
    return index === this.selectedIndex ? "picker-option picker-option--active" : "picker-option"
  }

  optionContent(option, index) {
    const isCurrent = String(option.value) === String(this.hiddenTarget.value)
    return `<span class="min-w-0">${this.renderOptionBody(option)}</span>
      ${isCurrent ? '<i class="ti ti-check picker-option__check" aria-hidden="true"></i>' : ''}`
  }

  scrollHighlightedIntoView() {
    const highlighted = this.listTarget.children[this.selectedIndex]
    if (highlighted) highlighted.scrollIntoView({ block: "nearest" })
  }

  // Renders an option's visible body. Structured options (with reference / aka /
  // suggested fields) get a two-line layout; plain {value, label} options fall
  // back to a single label line so other usages keep working unchanged.
  renderOptionBody(option) {
    const hasStructure = "reference" in option || "aka" in option || "suggested" in option
    if (!hasStructure) {
      return `<span class="break-words">${this.escapeHtml(option.label)}</span>`
    }

    const refPart = option.reference
      ? `<span class="picker-option__meta">${this.escapeHtml(option.reference)}</span>`
      : ""
    const suggestedPill = option.suggested
      ? '<span class="picker-option__pill">Suggested</span>'
      : ""

    const primary = `
      <span class="flex items-center gap-2 min-w-0">
        <span class="font-medium truncate">${this.escapeHtml(option.label)}</span>
        ${refPart}
        ${suggestedPill}
      </span>`

    const aka = Array.isArray(option.aka) ? option.aka : []
    let secondary = ""
    if (aka.length > 0) {
      const preview = aka.slice(0, 2).map(n => this.escapeHtml(n)).join(", ")
      const extra = aka.length > 2 ? ` <span class="font-medium">+${aka.length - 2} more</span>` : ""
      secondary = `<span class="picker-option__meta block truncate mt-0.5">Also known as ${preview}${extra}</span>`
    }

    return `${primary}${secondary}`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
